import { useEffect, useState } from 'react';
import { animations } from '../components/assets/Animations';
import { appOverride } from './AppUtils';
import { recordError } from './Errors';

type BackgroundData = { type: 'lottie'; data: unknown } | { type: 'svg'; path: string } | null;

/**
 * Custom hook to load and manage Nerva background animations.
 * Only returns backgrounds for Nerva - returns null for other apps (Meno, Quit, Pain).
 * Mobile uses SVG for better performance, desktop uses Lottie animation.
 * Automatically switches between mobile and desktop based on screen size.
 *
 * @returns The background data (type + data/path), or null if not Nerva or still loading
 */
export function useNervaBackgroundAnimation(): BackgroundData {
  const [backgroundData, setBackgroundData] = useState<BackgroundData>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Load Nerva background (returns null for other apps)
  useEffect(
    function loadBackground() {
      const backgroundPath = appOverride({
        nerva: isMobile ? animations.nervaBackgroundMobile : animations.nervaBackgroundDesktop,
        default: null,
      });

      // No background for non-Nerva apps
      if (backgroundPath === null) {
        setBackgroundData(null);
        return;
      }

      // SVG files don't need fetching, just use the path
      if (backgroundPath.endsWith('.svg')) {
        setBackgroundData({ type: 'svg', path: backgroundPath });
        return;
      }

      // Lottie animations need to be fetched
      fetch(backgroundPath)
        .then((response) => response.json())
        .then((data) => setBackgroundData({ type: 'lottie', data }))
        .catch((error) => recordError(error));
    },
    [isMobile]
  );

  // Handle window resize to switch between mobile and desktop
  useEffect(function handleResize() {
    const handleResizeEvent = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResizeEvent);
    return () => window.removeEventListener('resize', handleResizeEvent);
  }, []);

  return backgroundData;
}
